import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useEffect, useRef } from 'react'

import { NotificationService } from '@/contentComponents/global/Notification'
import type { AchievementReport } from '@/dto/AchievementAnalysis/AchievementReport.dto.ts'
import { LessonGroupService } from '@/page/Lesson/_service'
import ReportListService from '@/page/Lesson/Report/ReportList/ReportList.service'
import commonRepo from '@/repository/common.repo'
import { CustomEventService } from '@/service/customEvent.service'
import { reissueSingleFlightScheduler } from '@/utils/axios/jwtInterceptors'
import { envUtils } from '@/utils/envUtils'

import useDynamicRepo from './useDynamicRepo'
import { useFreshComponentRef } from './useFreshComponentRef'
import useNotificationSync from './useNotificationSync'
import useRepository from './useRepository'

// 재시도 불가능한 에러를 구분하기 위한 타입.
// FatalError는 다시 throw되어 SSE 재연결을 중단한다.
class FatalError extends Error {}

type SseEventType =
  | 'NOTIFICATION'
  | 'REPORT'
  | 'WORKBOOK_PDF'
  | 'CONTRACT_PDF'
  | 'ACHIEVEMENT_REPORT'
  | 'CUSTOM_SIGNATURE_WORKBOOK_THUMBNAIL'
  | 'CUSTOM_WORKBOOK_THUMBNAIL'

type SseEnvelope = {
  eventType: SseEventType
  version: number
  occurredAt: string
  payload: Record<string, unknown>
}

const getNotificationApiBaseUrl = () => {
  if (envUtils.isLive || localStorage.getItem('DEV_API') === 'live')
    return `https://notification-api.${domainProvider.TARGET_DOMAIN}`
  if (envUtils.isStaging || localStorage.getItem('DEV_API') === 'stg')
    return `https://notification-api-staging.${domainProvider.TARGET_DOMAIN}`
  return `https://notification-api-dev.${domainProvider.TARGET_DOMAIN}`
}

const getNotificationStreamUrl = () =>
  `${getNotificationApiBaseUrl()}/notifications/stream?platform=MATHFLAT`

const getJwt = () => jwtTokenService.getAccessToken()

const useSSE = () => {
  useNotificationSync()

  const notificationService = useRepository(NotificationService)
  const lessonGroupService = useRepository(LessonGroupService)
  const reportListService = useDynamicRepo([ReportListService, lessonGroupService.groupKey])
  const reportLiveServiceRef = useFreshComponentRef(reportListService)
  const activeControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!commonRepo.user?.teacherId) return

    // 이미 활성 연결이 있으면 중복 연결 방지
    if (activeControllerRef.current && !activeControllerRef.current.signal.aborted) return

    let currentController: AbortController
    let shutdownTimer: ReturnType<typeof setTimeout> | null = null
    // cleanup 이후 예약된 connect() 실행 방지
    let canConnect = true

    const connect = () => {
      if (!canConnect) return
      currentController = new AbortController()
      activeControllerRef.current = currentController

      fetchEventSource(getNotificationStreamUrl(), {
        headers: {
          Authorization: `Bearer ${getJwt()}`,
        },
        signal: currentController.signal,
        openWhenHidden: true,
        onopen: async (res) => {
          if (res.status === 401) {
            try {
              await reissueSingleFlightScheduler.start(() => jwtTokenService.reissueTokens())
            } catch {
              throw new FatalError('SSE 401: 토큰 재발급 실패')
            }
            // abort 후 connect() 재호출 → getJwt()가 새 토큰으로 재평가됨
            // throw 방식은 라이브러리가 구 헤더로 재시도하기 때문에 사용 불가
            currentController.abort()
            setTimeout(connect, 0)
            return
          }
          if (res.status === 403) {
            throw new FatalError('SSE 403: 접근 불가 플랫폼')
          }
          if (res.status >= 400) {
            throw new FatalError(`SSE open 실패: ${res.status}`)
          }
          notificationService.fetchNotifications()
          notificationService.fetchUnreadCount()
        },
        onmessage: (e) => {
          if (e.event === 'shutdown') {
            // 서버 롤링 배포 신호 — 서버가 배포가 끝나면 서버가 보내는 retry 값으로 재연결 딜레이 결정
            currentController.abort()
            shutdownTimer = setTimeout(connect, e.retry ?? 0)
            return
          }

          if (!e.data) return

          let envelope: SseEnvelope
          try {
            envelope = JSON.parse(e.data) as SseEnvelope
          } catch {
            return
          }

          const { eventType, payload } = envelope

          try {
            switch (eventType) {
              case 'NOTIFICATION':
                // NOTIFICATION은 payload 자체가 res.Notification (다른 이벤트와 달리 message 래퍼 없음)
                notificationService.receiveNotification(payload as unknown as res.Notification)
                break
              case 'REPORT':
                reportLiveServiceRef.current.requestRefetchReportListItemsById(
                  payload.message as number[],
                )
                break
              case 'WORKBOOK_PDF':
                CustomEventService.내교재PDF생성상태.dispatch(
                  payload.message as res.NotificationWithStatus<{
                    id: WorkbookEntity.Workbook['id']
                  }>,
                )
                break
              case 'CONTRACT_PDF':
                CustomEventService.스쿨플랫계약서생성상태.dispatch(
                  payload.message as res.NotificationWithStatus,
                )
                break
              case 'ACHIEVEMENT_REPORT':
                CustomEventService.유형분석보고서생성상태.dispatch(
                  payload.message as res.NotificationWithStatus<{ id: AchievementReport['id'] }>,
                )
                break
              case 'CUSTOM_SIGNATURE_WORKBOOK_THUMBNAIL':
                CustomEventService.커스텀시그니처교재썸네일생성상태.dispatch(
                  payload.message as res.NotificationWithStatus<{
                    id: WorkbookEntity.CustomSignatureWorkbook['id']
                  }>,
                )
                break
              case 'CUSTOM_WORKBOOK_THUMBNAIL':
                CustomEventService.커스텀교재썸네일생성상태.dispatch(
                  payload.message as res.NotificationWithStatus<{
                    id: WorkbookEntity.CustomSignatureWorkbook['id']
                  }>,
                )
                break
            }
          } catch (err) {
            // 이벤트 처리 중 에러가 SSE 재연결을 유발하지 않도록 catch
            console.error('[useSSE] onmessage error:', eventType, err)
          }
        },
        onerror: (err) => {
          if (err instanceof FatalError) throw err
        },
      }).catch(() => {})
    }

    connect()

    return () => {
      canConnect = false
      currentController.abort()
      activeControllerRef.current = null
      if (shutdownTimer) clearTimeout(shutdownTimer)
    }
  }, [commonRepo.user?.teacherId])
}

export default useSSE
